import {useState} from 'react';
import {Member} from '@/models/member';
import {Button} from '@/components/tailwind/button';
import {Text} from '@/components/tailwind/text';
import {Subheading} from '@/components/tailwind/heading';
import {CheckCircleIcon, XCircleIcon} from '@heroicons/react/24/outline';
import {
    grantMediaConsent,
    grantPrivacyConsent,
    revokeMediaConsent,
    revokePrivacyConsent,
    signNDA
} from '@/services/member-consent-services';
import {useAuth} from '@/context/use-auth';
import {useMembersTranslation} from '@/i18n/hooks';
import {format} from 'date-fns';
import {de} from 'date-fns/locale';
import {MemberStatus} from '@/models/member-status';

interface MemberConsentSectionProps {
    member: Member;
    onUpdate: () => void;
    ndaReminderDays?: number;
}

export default function MemberConsentSection({ member, onUpdate, ndaReminderDays = 30 }: MemberConsentSectionProps) {
    const { t } = useMembersTranslation();
    const { token } = useAuth();
    const [isLoading, setIsLoading] = useState(false);

    const formatDate = (dateString?: string) => {
        if (!dateString) return null;
        return format(new Date(dateString), 'dd.MM.yyyy', { locale: de });
    };

    const calculateDaysSinceMembership = () => {
        if (!member.membershipStartDate) return 0;
        const start = new Date(member.membershipStartDate);
        const today = new Date();
        const diffTime = Math.abs(today.getTime() - start.getTime());
        return Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    };

    const isNDAOverdue = () => {
        if (member.memberStatus !== MemberStatus.ACTIVE_MEMBER) return false;
        if (member.ndaSignedAt) return false;
        return calculateDaysSinceMembership() > ndaReminderDays;
    };

    const handlePrivacyConsent = async (grant: boolean) => {
        if (!token || !member.id) return;
        setIsLoading(true);
        
        const result = grant 
            ? await grantPrivacyConsent(token, member.id)
            : await revokePrivacyConsent(token, member.id);
        
        if (result) {
            onUpdate();
        }
        setIsLoading(false);
    };

    const handleMediaConsent = async (grant: boolean) => {
        if (!token || !member.id) return;
        setIsLoading(true);
        
        const result = grant 
            ? await grantMediaConsent(token, member.id)
            : await revokeMediaConsent(token, member.id);
        
        if (result) {
            onUpdate();
        }
        setIsLoading(false);
    };

    const handleSignNDA = async () => {
        if (!token || !member.id) return;
        setIsLoading(true);
        
        const result = await signNDA(token, member.id);
        
        if (result) {
            onUpdate();
        }
        setIsLoading(false);
    };

    return (
        <div className="space-y-6">
            <Subheading>{t('memberForm.fields.consent.privacy.label')}</Subheading>
            
            {/* Privacy Agreement */}
            <div className="flex items-center justify-between p-4 bg-gray-50 dark:bg-gray-800 rounded-lg">
                <div className="flex items-center space-x-3">
                    {member.privacyAgreementTimestamp ? (
                        <CheckCircleIcon className="h-6 w-6 text-green-500" />
                    ) : (
                        <XCircleIcon className="h-6 w-6 text-gray-400" />
                    )}
                    <div>
                        <Text className="font-medium">{t('memberForm.fields.consent.privacy.label')}</Text>
                        <Text className="text-sm text-gray-600 dark:text-gray-400">
                            {member.privacyAgreementTimestamp 
                                ? t('memberForm.fields.consent.privacy.granted', { date: formatDate(member.privacyAgreementTimestamp) })
                                : t('memberForm.fields.consent.privacy.notGranted')}
                        </Text>
                    </div>
                </div>
                <Button
                    onClick={() => handlePrivacyConsent(!member.privacyAgreementTimestamp)}
                    disabled={isLoading}
                    color={member.privacyAgreementTimestamp ? 'zinc' : 'indigo'}
                    data-testid="member-privacy-consent-button"
                >
                    {member.privacyAgreementTimestamp 
                        ? t('memberForm.fields.consent.privacy.revoke')
                        : t('memberForm.fields.consent.privacy.grant')}
                </Button>
            </div>

            {/* Media Consent */}
            <div className="flex items-center justify-between p-4 bg-gray-50 dark:bg-gray-800 rounded-lg">
                <div className="flex items-center space-x-3">
                    {member.mediaConsentTimestamp ? (
                        <CheckCircleIcon className="h-6 w-6 text-green-500" />
                    ) : (
                        <XCircleIcon className="h-6 w-6 text-gray-400" />
                    )}
                    <div>
                        <Text className="font-medium">{t('memberForm.fields.consent.media.label')}</Text>
                        <Text className="text-sm text-gray-600 dark:text-gray-400">
                            {member.mediaConsentTimestamp 
                                ? t('memberForm.fields.consent.media.granted', { date: formatDate(member.mediaConsentTimestamp) })
                                : t('memberForm.fields.consent.media.notGranted')}
                        </Text>
                    </div>
                </div>
                <Button
                    onClick={() => handleMediaConsent(!member.mediaConsentTimestamp)}
                    disabled={isLoading}
                    color={member.mediaConsentTimestamp ? 'zinc' : 'indigo'}
                    data-testid="member-media-consent-button"
                >
                    {member.mediaConsentTimestamp 
                        ? t('memberForm.fields.consent.media.revoke')
                        : t('memberForm.fields.consent.media.grant')}
                </Button>
            </div>

            {/* NDA - Only for active members */}
            {member.memberStatus === MemberStatus.ACTIVE_MEMBER && (
                <div className={`flex items-center justify-between p-4 rounded-lg ${
                    isNDAOverdue() 
                        ? 'bg-amber-50 dark:bg-amber-950 border-l-4 border-amber-400 dark:border-amber-500' 
                        : 'bg-gray-50 dark:bg-gray-800'
                }`}>
                    <div className="flex items-center space-x-3">
                        {member.ndaSignedAt ? (
                            <CheckCircleIcon className="h-6 w-6 text-green-500" />
                        ) : (
                            <XCircleIcon className={`h-6 w-6 ${isNDAOverdue() ? 'text-amber-500' : 'text-gray-400'}`} />
                        )}
                        <div>
                            <Text className="font-medium">{t('memberForm.fields.nda.label')}</Text>
                            <Text className="text-sm text-gray-600 dark:text-gray-400">
                                {member.ndaSignedAt 
                                    ? t('memberForm.fields.nda.signed', { date: formatDate(member.ndaSignedAt) })
                                    : isNDAOverdue()
                                        ? t('memberForm.fields.nda.warning', { days: calculateDaysSinceMembership() })
                                        : t('memberForm.fields.nda.notSigned')}
                            </Text>
                        </div>
                    </div>
                    {!member.ndaSignedAt && (
                        <Button
                            onClick={handleSignNDA}
                            disabled={isLoading}
                            color={isNDAOverdue() ? 'amber' : 'indigo'}
                            data-testid="member-nda-sign-button"
                        >
                            {t('memberForm.fields.nda.sign')}
                        </Button>
                    )}
                </div>
            )}
        </div>
    );
}
